import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState } from "react";
import {
  Plus, Mail, Users, UserPlus, FileText, Send, Trash2, BarChart3,
  TrendingUp, Globe, Eye, MousePointerClick, Check, Copy, Search,
  Zap, Clock, ArrowUpRight, ArrowDownRight, Shield, Activity,
  Sparkles, Play, Pause, Calendar, DollarSign, ChevronRight, ExternalLink,
  MailCheck, MailX, AlertTriangle, RefreshCw, Layers, Save, Gauge, ArrowUpDown,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "lists", label: "Lists", icon: Users },
  { id: "subscribers", label: "Subscribers", icon: UserPlus },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "campaigns", label: "Campaigns", icon: Send },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "domains", label: "Domains", icon: Globe },
] as const;
type Tab = (typeof TABS)[number]["id"];

const CATEGORY_COLORS: Record<string, string> = { welcome: "bg-green-100 text-green-700", newsletter: "bg-blue-100 text-blue-700", promo: "bg-orange-100 text-orange-700", general: "bg-gray-100 text-gray-700", "re-engagement": "bg-purple-100 text-purple-700", transactional: "bg-cyan-100 text-cyan-700", announcement: "bg-pink-100 text-pink-700" };
const STATUS_COLORS: Record<string, string> = { draft: "bg-gray-100 text-gray-600", scheduled: "bg-blue-100 text-blue-700", sending: "bg-yellow-100 text-yellow-700", sent: "bg-green-100 text-green-700", paused: "bg-orange-100 text-orange-700", cancelled: "bg-red-100 text-red-600", active: "bg-green-100 text-green-700", archived: "bg-gray-100 text-gray-500", pending: "bg-yellow-100 text-yellow-700", verified: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-600" };
const DEVICE_COLORS = ["#8B5CF6", "#EC4899", "#3b82f6", "#f59e0b"];
const fmt = (n: number) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

export default function EmailMarketing() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [selectedListId, setSelectedListId] = useState<string>("");
  const [selectedCampId, setSelectedCampId] = useState<string>("");
  const [showCreateList, setShowCreateList] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showPreview, setShowPreview] = useState<any>(null);
  const [campStatusFilter, setCampStatusFilter] = useState("all");
  const [campTypeFilter, setCampTypeFilter] = useState("all");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campSortBy, setCampSortBy] = useState("updatedAt");
  const [campSortDir, setCampSortDir] = useState<"asc" | "desc">("desc");
  const [viewName, setViewName] = useState("");
  const [viewDefault, setViewDefault] = useState(false);
  const [subSearch, setSubSearch] = useState("");
  const [subStatusFilter, setSubStatusFilter] = useState("all");
  const [tplCategoryFilter, setTplCategoryFilter] = useState("all");
  const [listForm, setListForm] = useState({ name: "", description: "", doubleOptIn: false });
  const [tplForm, setTplForm] = useState({ name: "", subject: "", preheader: "", htmlBody: "", category: "newsletter" });
  const [campForm, setCampForm] = useState({ name: "", type: "regular", templateId: "", listId: "", fromName: "", fromEmail: "", subject: "", preheader: "", scheduledAt: "" });
  const [subForm, setSubForm] = useState({ email: "", firstName: "", lastName: "", tags: "" });
  const [domainForm, setDomainForm] = useState({ domain: "" });
  const [copiedDns, setCopiedDns] = useState("");

  // ── Queries ───────────────────────────────────
  const { data: overview } = useQuery({ queryKey: ["email-overview"], queryFn: () => api.get<any>("/email/overview"), enabled: tab === "overview" });
  const { data: enterprise } = useQuery({ queryKey: ["email-enterprise"], queryFn: () => api.get<any>("/email/enterprise") });
  const { data: growth } = useQuery({ queryKey: ["email-growth"], queryFn: () => api.get<any[]>("/email/growth-timeline"), enabled: tab === "overview" });
  const { data: engagement } = useQuery({ queryKey: ["email-engagement"], queryFn: () => api.get<any[]>("/email/engagement-timeline"), enabled: tab === "overview" });
  const { data: lists } = useQuery({ queryKey: ["email-lists"], queryFn: () => api.get<any[]>("/email/lists") });
  const { data: subsData } = useQuery({ queryKey: ["email-subs", selectedListId, subSearch, subStatusFilter], queryFn: () => api.get<any>(`/email/lists/${selectedListId}/subscribers?search=${subSearch}&status=${subStatusFilter === "all" ? "" : subStatusFilter}`), enabled: !!selectedListId && tab === "subscribers" });
  const { data: templates } = useQuery({ queryKey: ["email-templates"], queryFn: () => api.get<any[]>("/email/templates"), enabled: tab === "templates" || tab === "campaigns" || tab === "overview" });
  const { data: campaigns } = useQuery({
    queryKey: ["email-campaigns", campTypeFilter, campaignSearch, campSortBy, campSortDir],
    queryFn: () => api.get<any[]>(`/email/campaigns?${campTypeFilter !== "all" ? `type=${campTypeFilter}&` : ""}${campaignSearch ? `search=${encodeURIComponent(campaignSearch)}&` : ""}${campSortBy ? `sortBy=${campSortBy}&` : ""}${campSortDir ? `sortDir=${campSortDir}` : ""}`),
  });
  const { data: campDetail } = useQuery({ queryKey: ["email-camp-detail", selectedCampId], queryFn: () => api.get<any>(`/email/campaigns/${selectedCampId}`), enabled: !!selectedCampId && tab === "analytics" });
  const { data: campAnalytics } = useQuery({ queryKey: ["email-camp-analytics", selectedCampId], queryFn: () => api.get<any>(`/email/campaigns/${selectedCampId}/analytics`), enabled: !!selectedCampId && tab === "analytics" });
  const { data: domains } = useQuery({ queryKey: ["email-domains"], queryFn: () => api.get<any[]>("/email/domains"), enabled: tab === "domains" });
  const { data: emailViews } = useQuery({ queryKey: ["email-views"], queryFn: () => api.get<any>("/email/views") });

  // ── Mutations ──────────────────────────────────
  const inv = (keys: string[]) => { keys.forEach(k => qc.invalidateQueries({ queryKey: [k] })); };
  const createListM = useMutation({ mutationFn: (b: any) => api.post("/email/lists", b), onSuccess: () => { inv(["email-lists", "email-overview"]); setShowCreateList(false); setListForm({ name: "", description: "", doubleOptIn: false }); } });
  const deleteListM = useMutation({ mutationFn: (id: string) => api.delete(`/email/lists/${id}`), onSuccess: () => inv(["email-lists", "email-overview"]) });
  const createTplM = useMutation({ mutationFn: (b: any) => api.post("/email/templates", b), onSuccess: () => { inv(["email-templates"]); setShowCreateTemplate(false); setTplForm({ name: "", subject: "", preheader: "", htmlBody: "", category: "newsletter" }); } });
  const dupTplM = useMutation({ mutationFn: (id: string) => api.post(`/email/templates/${id}/duplicate`, {}), onSuccess: () => inv(["email-templates"]) });
  const deleteTplM = useMutation({ mutationFn: (id: string) => api.delete(`/email/templates/${id}`), onSuccess: () => inv(["email-templates"]) });
  const createCampM = useMutation({ mutationFn: (b: any) => api.post("/email/campaigns", b), onSuccess: () => { inv(["email-campaigns", "email-overview", "email-enterprise"]); setShowCreateCampaign(false); setCampForm({ name: "", type: "regular", templateId: "", listId: "", fromName: "", fromEmail: "", subject: "", preheader: "", scheduledAt: "" }); } });
  const deleteCampM = useMutation({ mutationFn: (id: string) => api.delete(`/email/campaigns/${id}`), onSuccess: () => inv(["email-campaigns", "email-overview", "email-enterprise"]) });
  const sendCampM = useMutation({ mutationFn: (id: string) => api.post(`/email/campaigns/${id}/send`, {}), onSuccess: () => inv(["email-campaigns", "email-overview", "email-enterprise"]) });
  const addSubM = useMutation({ mutationFn: (b: any) => api.post(`/email/lists/${selectedListId}/subscribers`, b), onSuccess: () => { inv(["email-subs", "email-lists", "email-overview"]); setShowAddSubscriber(false); setSubForm({ email: "", firstName: "", lastName: "", tags: "" }); } });
  const deleteSubM = useMutation({ mutationFn: (id: string) => api.delete(`/email/subscribers/${id}`), onSuccess: () => inv(["email-subs", "email-lists", "email-overview"]) });
  const addDomainM = useMutation({ mutationFn: (b: any) => api.post("/email/domains", b), onSuccess: () => { inv(["email-domains", "email-enterprise"]); setShowAddDomain(false); setDomainForm({ domain: "" }); } });
  const verifyDomainM = useMutation({ mutationFn: (id: string) => api.post(`/email/domains/${id}/verify`, {}), onSuccess: () => inv(["email-domains", "email-enterprise"]) });
  const deleteDomainM = useMutation({ mutationFn: (id: string) => api.delete(`/email/domains/${id}`), onSuccess: () => inv(["email-domains", "email-enterprise"]) });
  const createViewM = useMutation({
    mutationFn: (body: any) => api.post("/email/views", body),
    onSuccess: () => {
      inv(["email-views"]);
      setViewName("");
      setViewDefault(false);
    },
  });
  const updateViewM = useMutation({
    mutationFn: ({ id, ...body }: any) => api.put(`/email/views/${id}`, body),
    onSuccess: () => inv(["email-views"]),
  });
  const deleteViewM = useMutation({
    mutationFn: (id: string) => api.delete(`/email/views/${id}`),
    onSuccess: () => inv(["email-views"]),
  });


  const copyDns = (text: string, label: string) => { navigator.clipboard.writeText(text); setCopiedDns(label); setTimeout(() => setCopiedDns(""), 2000); };

  // ── KPI Card ──────────────────────────────────
  const KPI = ({ icon: Icon, label, value, sub, color = "blue" }: any) => (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}><Icon className={`w-5 h-5 text-${color}-600`} /></div>
        <div><p className="text-2xl font-bold">{value}</p><p className="text-xs text-gray-500">{label}</p></div>
      </div>
      {sub && <p className="text-xs text-gray-400 mt-2">{sub}</p>}
    </div>
  );

  // ── Badge ─────────────────────────────────────
  const Badge = ({ text, map }: { text: string; map: Record<string, string> }) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[text] || "bg-gray-100 text-gray-600"}`}>{text}</span>
  );

  const currentCampaignFilters = { campStatusFilter, campTypeFilter, campaignSearch, campSortBy, campSortDir };
  const applyCampaignView = (view: any) => {
    const f = view?.filters || {};
    setCampStatusFilter(f.campStatusFilter || "all");
    setCampTypeFilter(f.campTypeFilter || "all");
    setCampaignSearch(f.campaignSearch || "");
    setCampSortBy(f.campSortBy || "updatedAt");
    setCampSortDir(f.campSortDir === "asc" ? "asc" : "desc");
  };
  const isCampaignViewActive = (view: any) => {
    const f = view?.filters || {};
    return (
      (f.campStatusFilter || "all") === campStatusFilter &&
      (f.campTypeFilter || "all") === campTypeFilter &&
      (f.campaignSearch || "") === campaignSearch &&
      (f.campSortBy || "updatedAt") === campSortBy &&
      (f.campSortDir === "asc" ? "asc" : "desc") === campSortDir
    );
  };
  const campaignSignal = (campaign: any) => {
    if (campaign.status === "sending" && new Date(campaign.updatedAt).getTime() < Date.now() - 2 * 60 * 60 * 1000) {
      return { label: "Send stalled", cls: "bg-amber-100 text-amber-700" };
    }
    if (campaign.status === "sent" && (campaign.bounceRate >= 2 || campaign.openRate < 15)) {
      return { label: "Performance risk", cls: "bg-red-100 text-red-700" };
    }
    if (campaign.status === "draft" && new Date(campaign.updatedAt).getTime() < Date.now() - 7 * 86400000) {
      return { label: "Stale draft", cls: "bg-yellow-100 text-yellow-700" };
    }
    return { label: "Healthy", cls: "bg-green-100 text-green-700" };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
          <p className="text-sm text-gray-500 mt-1">Enterprise email campaigns, lists, templates & analytics</p>
        </div>
        <div className="flex gap-2">

          <button onClick={() => {
            if (tab === "lists") setShowCreateList(true);
            else if (tab === "templates") setShowCreateTemplate(true);
            else if (tab === "campaigns") setShowCreateCampaign(true);
            else if (tab === "subscribers") setShowAddSubscriber(true);
            else if (tab === "domains") setShowAddDomain(true);
          }} className="btn-primary"><Plus className="w-4 h-4" /> New {tab === "lists" ? "List" : tab === "templates" ? "Template" : tab === "campaigns" ? "Campaign" : tab === "subscribers" ? "Subscriber" : tab === "domains" ? "Domain" : ""}</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* ═══════ OVERVIEW TAB ═══════ */}
      {tab === "overview" && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Users} label="Total Subscribers" value={fmt(overview.totalSubscribers)} sub={`${fmt(overview.activeSubscribers)} active`} color="blue" />
            <KPI icon={Layers} label="Email Lists" value={overview.totalLists} color="violet" />
            <KPI icon={Send} label="Campaigns Sent" value={overview.sentCampaigns} sub={`${overview.totalCampaigns} total`} color="green" />
            <KPI icon={Eye} label="Avg Open Rate" value={`${overview.avgOpenRate}%`} sub="Industry avg: 21.3%" color="amber" />
            <KPI icon={MousePointerClick} label="Avg Click Rate" value={`${overview.avgClickRate}%`} sub="Industry avg: 2.6%" color="pink" />
            <KPI icon={Shield} label="Deliverability" value={`${overview.deliverability}%`} color="emerald" />
            <KPI icon={MailX} label="Bounced" value={overview.totalBounced} color="red" />
            <KPI icon={DollarSign} label="Revenue" value={`$${fmt(overview.totalRevenue)}`} color="green" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Gauge className="w-4 h-4 text-indigo-600" /> Command Score</h3>
              <p className="text-3xl font-bold text-gray-900 mt-2">{enterprise?.commandScore ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">Enterprise email operations readiness</p>
            </div>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Risk Radar</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between"><span className="text-gray-500">Stale drafts</span><span className="font-semibold text-amber-600">{enterprise?.risks?.staleDrafts ?? 0}</span></div>
                <div className="flex items-center justify-between"><span className="text-gray-500">Send SLA breaches</span><span className="font-semibold text-red-600">{enterprise?.risks?.sendingOverSla ?? 0}</span></div>
                <div className="flex items-center justify-between"><span className="text-gray-500">Hygiene alerts</span><span className="font-semibold text-red-600">{enterprise?.risks?.hygieneAlerts ?? 0}</span></div>
              </div>
            </div>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Automation Footprint</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between"><span className="text-gray-500">Automation coverage</span><span className="font-semibold text-green-600">{enterprise?.performance?.automationCoveragePct ?? 0}%</span></div>
                <div className="flex items-center justify-between"><span className="text-gray-500">Deliverability guard</span><span className="font-semibold text-indigo-600">{enterprise?.performance?.deliverabilityGuardPct ?? 0}%</span></div>
                <div className="flex items-center justify-between"><span className="text-gray-500">7d send velocity</span><span className="font-semibold text-blue-600">{fmt(enterprise?.performance?.sendVelocity7d || 0)}</span></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Subscriber Growth */}
            <div className="card p-5 lg:col-span-2">
              <h3 className="font-semibold mb-4">Subscriber Growth (30 days)</h3>
              {growth?.length ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={growth}>
                    <defs><linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="total" stroke="#8B5CF6" fill="url(#growthGrad)" strokeWidth={2} name="Total" />
                    <Area type="monotone" dataKey="newSubscribers" stroke="#10b981" fill="none" strokeWidth={1.5} strokeDasharray="4 2" name="New" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm">No data yet</p>}
            </div>
            {/* Device Breakdown */}
            <div className="card p-5">
              <h3 className="font-semibold mb-4">Device Breakdown</h3>
              {overview.deviceBreakdown && Object.keys(overview.deviceBreakdown).length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={Object.entries(overview.deviceBreakdown).map(([name, value]) => ({ name, value }))} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {Object.keys(overview.deviceBreakdown).map((_, i) => <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-sm">No device data yet</p>}
            </div>
          </div>

          {/* Engagement Trend */}
          {engagement?.length ? (
            <div className="card p-5">
              <h3 className="font-semibold mb-4">Engagement Trend (30 days)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={engagement}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="sent" stroke="#64748b" fill="none" strokeWidth={1.5} name="Sent" />
                  <Area type="monotone" dataKey="opened" stroke="#8B5CF6" fill="none" strokeWidth={2} name="Opened" />
                  <Area type="monotone" dataKey="clicked" stroke="#EC4899" fill="none" strokeWidth={2} name="Clicked" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          {/* Recent Campaigns */}
          {overview.recentCampaigns?.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold">Recent Campaigns</h3>
                <button onClick={() => setTab("campaigns")} className="text-xs text-blue-600 hover:underline flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></button>
              </div>
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b border-gray-200"><th className="text-left px-5 py-2 text-xs font-medium text-gray-500 uppercase">Campaign</th><th className="text-left px-5 py-2 text-xs font-medium text-gray-500 uppercase">Status</th><th className="text-left px-5 py-2 text-xs font-medium text-gray-500 uppercase">Open Rate</th><th className="text-left px-5 py-2 text-xs font-medium text-gray-500 uppercase">Click Rate</th><th className="text-left px-5 py-2 text-xs font-medium text-gray-500 uppercase">Date</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {overview.recentCampaigns.map((c: any) => (
                    <tr key={c.id} className="hover:bg-gray-50"><td className="px-5 py-2.5 text-sm font-medium">{c.name}</td><td className="px-5 py-2.5"><Badge text={c.status} map={STATUS_COLORS} /></td><td className="px-5 py-2.5 text-sm">{c.openRate > 0 ? `${c.openRate}%` : "–"}</td><td className="px-5 py-2.5 text-sm">{c.clickRate > 0 ? `${c.clickRate}%` : "–"}</td><td className="px-5 py-2.5 text-xs text-gray-400">{c.sentAt ? new Date(c.sentAt).toLocaleDateString() : c.scheduledAt ? `Sched: ${new Date(c.scheduledAt).toLocaleDateString()}` : "Draft"}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══════ LISTS TAB ═══════ */}
      {tab === "lists" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists?.length ? lists.map((list: any) => (
            <div key={list.id} className="card p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                <div className="flex items-center gap-2">
                  <Badge text={list.status} map={STATUS_COLORS} />
                  <button onClick={() => { if (confirm("Delete this list and all subscribers?")) deleteListM.mutate(list.id); }} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <h3 className="font-semibold">{list.name}</h3>
              {list.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{list.description}</p>}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-sm">
                <span className="text-gray-600"><strong>{list._count?.subscribers || list.subscriberCount || 0}</strong> subscribers</span>
                <span className="text-gray-400">{list._count?.campaigns || 0} campaigns</span>
                {list.growthRate !== 0 && (
                  <span className={`flex items-center gap-0.5 text-xs ${list.growthRate > 0 ? "text-green-600" : "text-red-500"}`}>
                    {list.growthRate > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{Math.abs(list.growthRate)}%
                  </span>
                )}
              </div>
              {list.doubleOptIn && <p className="text-xs text-blue-500 mt-2 flex items-center gap-1"><Shield className="w-3 h-3" /> Double opt-in enabled</p>}
            </div>
          )) : (
            <div className="col-span-full card p-8 text-center">
              <Mail className="w-12 h-12 text-blue-400 mx-auto mb-3" /><p className="text-gray-500">No email lists yet</p>
              <button onClick={() => setShowCreateList(true)} className="btn-primary mt-3">Create First List</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════ SUBSCRIBERS TAB ═══════ */}
      {tab === "subscribers" && (
        <div className="space-y-4">
          {/* List selector + search */}
          <div className="flex flex-wrap gap-3 items-center">
            <select className="input max-w-xs" value={selectedListId} onChange={e => setSelectedListId(e.target.value)}>
              <option value="">Select a list...</option>
              {lists?.map((l: any) => <option key={l.id} value={l.id}>{l.name} ({l._count?.subscribers || 0})</option>)}
            </select>
            {selectedListId && (
              <>
                <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" /><input className="input pl-10" placeholder="Search by email or name..." value={subSearch} onChange={e => setSubSearch(e.target.value)} /></div>
                <select className="input max-w-[140px]" value={subStatusFilter} onChange={e => setSubStatusFilter(e.target.value)}>
                  <option value="all">All Status</option><option value="active">Active</option><option value="unsubscribed">Unsubscribed</option><option value="bounced">Bounced</option><option value="complained">Complained</option>
                </select>
              </>
            )}
          </div>

          {selectedListId && subsData ? (
            <>
              {/* Stats bar */}
              <div className="flex gap-4">
                <div className="card px-4 py-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500" /><span className="text-sm font-medium">{subsData.stats?.active || 0}</span><span className="text-xs text-gray-500">Active</span></div>
                <div className="card px-4 py-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500" /><span className="text-sm font-medium">{subsData.stats?.unsubscribed || 0}</span><span className="text-xs text-gray-500">Unsubscribed</span></div>
                <div className="card px-4 py-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-sm font-medium">{subsData.stats?.bounced || 0}</span><span className="text-xs text-gray-500">Bounced</span></div>
                <div className="card px-4 py-2"><span className="text-sm font-medium">{subsData.total}</span><span className="text-xs text-gray-500 ml-1">Total</span></div>
              </div>

              {/* Table */}
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Score</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Country</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Opens</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Clicks</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Subscribed</th>
                    <th className="px-4 py-2.5"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {subsData.subscribers?.map((s: any) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-sm font-medium">{s.email}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600">{s.firstName || s.name || "–"} {s.lastName || ""}</td>
                        <td className="px-4 py-2.5"><Badge text={s.status} map={STATUS_COLORS} /></td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${s.engagementScore >= 70 ? "bg-green-500" : s.engagementScore >= 40 ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${s.engagementScore}%` }} /></div><span className="text-xs text-gray-500">{s.engagementScore}</span></div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{s.source}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">{s.country || "–"}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500">{s.openCount}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500">{s.clickCount}</td>
                        <td className="px-4 py-2.5 text-xs text-gray-400">{new Date(s.subscribedAt).toLocaleDateString()}</td>
                        <td className="px-4 py-2.5"><button onClick={() => deleteSubM.mutate(s.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subsData.pages > 1 && <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">Page {subsData.page} of {subsData.pages} · {subsData.total} subscribers</div>}
              </div>
            </>
          ) : !selectedListId ? (
            <div className="card p-8 text-center"><UserPlus className="w-12 h-12 text-blue-400 mx-auto mb-3" /><p className="text-gray-500">Select a list to view subscribers</p></div>
          ) : null}
        </div>
      )}

      {/* ═══════ TEMPLATES TAB ═══════ */}
      {tab === "templates" && (
        <div className="space-y-4">
          {/* Category filter */}
          <div className="flex gap-2 flex-wrap">
            {["all", "welcome", "newsletter", "promo", "general", "re-engagement", "transactional"].map(c => (
              <button key={c} onClick={() => setTplCategoryFilter(c)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tplCategoryFilter === c ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{c === "all" ? "All" : c.charAt(0).toUpperCase() + c.slice(1)}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates?.filter((t: any) => tplCategoryFilter === "all" || t.category === tplCategoryFilter).map((t: any) => (
              <div key={t.id} className="card p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <Badge text={t.category} map={CATEGORY_COLORS} />
                  <Badge text={t.status} map={STATUS_COLORS} />
                </div>
                <h3 className="font-semibold mt-2">{t.name}</h3>
                <p className="text-sm text-gray-500 mt-1 truncate">Subject: {t.subject}</p>
                {t.preheader && <p className="text-xs text-gray-400 mt-1 truncate">Preheader: {t.preheader}</p>}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{t._count?.campaigns || 0} campaigns</span>
                  <span className="text-xs text-gray-400">Updated {new Date(t.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setShowPreview(t)} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</button>
                  <button onClick={() => dupTplM.mutate(t.id)} className="text-xs text-violet-600 hover:underline flex items-center gap-1"><Copy className="w-3 h-3" /> Duplicate</button>
                  <button onClick={() => { if (confirm("Delete template?")) deleteTplM.mutate(t.id); }} className="text-xs text-red-500 hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
            {(!templates || templates.filter((t: any) => tplCategoryFilter === "all" || t.category === tplCategoryFilter).length === 0) && (
              <div className="col-span-full card p-8 text-center">
                <FileText className="w-12 h-12 text-purple-400 mx-auto mb-3" /><p className="text-gray-500">No templates yet</p>
                <button onClick={() => setShowCreateTemplate(true)} className="btn-primary mt-3">Create Template</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ CAMPAIGNS TAB ═══════ */}
      {tab === "campaigns" && (() => {
        const visibleCampaigns = (campaigns || []).filter((c: any) => {
          const statusMatch = campStatusFilter === "all" || c.status === campStatusFilter;
          const typeMatch = campTypeFilter === "all" || c.type === campTypeFilter;
          const searchMatch = !campaignSearch || [c.name, c.subject, c?.list?.name, c?.template?.name].some((x) => String(x || "").toLowerCase().includes(campaignSearch.toLowerCase()));
          return statusMatch && typeMatch && searchMatch;
        });

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="card p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Gauge className="w-3.5 h-3.5 text-indigo-600" /> Command Score</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{enterprise?.commandScore ?? 0}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Stale Drafts</p>
                <p className="text-xl font-bold text-amber-600 mt-1">{enterprise?.risks?.staleDrafts ?? 0}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Send SLA Breaches</p>
                <p className="text-xl font-bold text-red-600 mt-1">{enterprise?.risks?.sendingOverSla ?? 0}</p>
              </div>
              <div className="card p-3">
                <p className="text-xs text-gray-500">Deliverability Guard</p>
                <p className="text-xl font-bold text-green-600 mt-1">{enterprise?.performance?.deliverabilityGuardPct ?? 0}%</p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {["all", "draft", "scheduled", "sent", "paused"].map((s) => (
                <button key={s} onClick={() => setCampStatusFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${campStatusFilter === s ? "bg-gradient-to-r from-violet-500 to-pink-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)} {s !== "all" && campaigns ? `(${campaigns.filter((c: any) => c.status === s).length})` : ""}</button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap items-center">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                <input className="input pl-9" placeholder="Search campaign, subject, list..." value={campaignSearch} onChange={(e) => setCampaignSearch(e.target.value)} />
              </div>
              <select className="input max-w-[180px]" value={campTypeFilter} onChange={(e) => setCampTypeFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="regular">Regular</option>
                <option value="ab_test">A/B Test</option>
                <option value="automated">Automated</option>
                <option value="rss">RSS</option>
              </select>
              <select className="input max-w-[190px]" value={campSortBy} onChange={(e) => setCampSortBy(e.target.value)}>
                <option value="updatedAt">Sort: Updated</option>
                <option value="createdAt">Sort: Created</option>
                <option value="scheduledAt">Sort: Scheduled</option>
                <option value="sentAt">Sort: Sent</option>
                <option value="openRate">Sort: Open Rate</option>
                <option value="clickRate">Sort: Click Rate</option>
                <option value="revenue">Sort: Revenue</option>
              </select>
              <button className="btn-secondary flex items-center gap-1" onClick={() => setCampSortDir(campSortDir === "desc" ? "asc" : "desc")}>
                <ArrowUpDown className="w-4 h-4" /> {campSortDir === "desc" ? "Desc" : "Asc"}
              </button>
            </div>

            <div className="card p-4">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Save className="w-4 h-4 text-purple-600" /> Saved Views</h3>
                <input className="input h-9 max-w-[220px]" placeholder="View name" value={viewName} onChange={(e) => setViewName(e.target.value)} />
                <label className="text-xs text-gray-600 flex items-center gap-1"><input type="checkbox" checked={viewDefault} onChange={(e) => setViewDefault(e.target.checked)} />Default</label>
                <button
                  className="btn-secondary h-9"
                  onClick={() => {
                    const name = viewName.trim();
                    if (!name) return;
                    createViewM.mutate({ name, filters: currentCampaignFilters, isDefault: viewDefault });
                  }}
                  disabled={!viewName.trim() || createViewM.isPending}
                >
                  Save View
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                {emailViews?.views?.length ? emailViews.views.map((v: any) => (
                  <div key={v.id} className={`px-3 py-2 rounded-xl border text-xs ${isCampaignViewActive(v) ? "border-violet-300 bg-violet-50" : "border-gray-200 bg-white"}`}>
                    <button className="font-medium text-gray-700" onClick={() => applyCampaignView(v)}>{v.name}</button>
                    <div className="flex gap-2 mt-1">
                      {!v.isDefault && <button className="text-gray-500 hover:text-violet-600" onClick={() => updateViewM.mutate({ id: v.id, isDefault: true })}>Default</button>}
                      <button className="text-red-500 hover:text-red-600" onClick={() => deleteViewM.mutate(v.id)}>Delete</button>
                    </div>
                  </div>
                )) : <p className="text-xs text-gray-400">No saved views yet.</p>}
              </div>
            </div>

            <div className="space-y-3">
              {visibleCampaigns.map((c: any) => {
                const signal = campaignSignal(c);
                return (
                  <div key={c.id} className="card p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold">{c.name}</h3>
                          <Badge text={c.status} map={STATUS_COLORS} />
                          <Badge text={c.type} map={{ regular: "bg-blue-50 text-blue-600", ab_test: "bg-purple-50 text-purple-600", automated: "bg-green-50 text-green-600", rss: "bg-orange-50 text-orange-600" }} />
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${signal.cls}`}>{signal.label}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          {c.template && <span>Template: {c.template.name}</span>}
                          {c.list && <span>List: {c.list.name}</span>}
                          {c.sentAt && <span>Sent: {new Date(c.sentAt).toLocaleDateString()}</span>}
                          {!c.sentAt && c.scheduledAt && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(c.scheduledAt).toLocaleDateString()}</span>}
                          {c.recipientCount > 0 && <span>{fmt(c.recipientCount)} recipients</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.status === "draft" && <button onClick={() => sendCampM.mutate(c.id)} className="text-xs text-green-600 hover:underline flex items-center gap-1"><Play className="w-3 h-3" /> Send</button>}
                        {c.status === "scheduled" && <button onClick={() => api.post(`/email/campaigns/${c.id}/pause`, {}).then(() => inv(["email-campaigns", "email-enterprise"]))} className="text-xs text-orange-600 hover:underline flex items-center gap-1"><Pause className="w-3 h-3" /> Pause</button>}
                        <button onClick={() => { if (confirm("Delete campaign?")) deleteCampM.mutate(c.id); }} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {c.status === "sent" && (
                      <div className="grid grid-cols-4 gap-4 mt-3 pt-3 border-t border-gray-100">
                        <div><p className="text-xs text-gray-500">Open Rate</p><p className="text-lg font-bold text-violet-600">{c.openRate}%</p></div>
                        <div><p className="text-xs text-gray-500">Click Rate</p><p className="text-lg font-bold text-pink-600">{c.clickRate}%</p></div>
                        <div><p className="text-xs text-gray-500">Bounce Rate</p><p className="text-lg font-bold text-red-500">{c.bounceRate}%</p></div>
                        <div><p className="text-xs text-gray-500">Revenue</p><p className="text-lg font-bold text-green-600">{c.revenue > 0 ? `$${fmt(c.revenue)}` : "–"}</p></div>
                        {c.type === "ab_test" && c.abWinner && (
                          <div className="col-span-4"><p className="text-xs text-purple-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> A/B Winner: Variant {c.abWinner.toUpperCase()} (by {c.abWinnerMetric?.replace("_", " ")})</p></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!visibleCampaigns.length && (
                <div className="card p-8 text-center">
                  <Send className="w-12 h-12 text-green-400 mx-auto mb-3" /><p className="text-gray-500">No campaigns match this view</p>
                  <button onClick={() => setShowCreateCampaign(true)} className="btn-primary mt-3">Create Campaign</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══════ ANALYTICS TAB ═══════ */}
      {tab === "analytics" && (
        <div className="space-y-4">
          <select className="input max-w-sm" value={selectedCampId} onChange={e => setSelectedCampId(e.target.value)}>
            <option value="">Select a campaign to analyze...</option>
            {campaigns?.filter((c: any) => c.status === "sent").map((c: any) => <option key={c.id} value={c.id}>{c.name} — {new Date(c.sentAt).toLocaleDateString()}</option>)}
          </select>

          {campDetail && campAnalytics ? (
            <div className="space-y-6">
              {/* Big metric cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card p-5 text-center border-t-4 border-violet-500"><p className="text-3xl font-bold text-violet-600">{campDetail.openRate}%</p><p className="text-sm text-gray-500 mt-1">Open Rate</p><p className="text-xs text-gray-400">{fmt(campDetail.uniqueOpens)} / {fmt(campDetail.sentCount)}</p></div>
                <div className="card p-5 text-center border-t-4 border-pink-500"><p className="text-3xl font-bold text-pink-600">{campDetail.clickRate}%</p><p className="text-sm text-gray-500 mt-1">Click Rate</p><p className="text-xs text-gray-400">{fmt(campDetail.uniqueClicks)} / {fmt(campDetail.sentCount)}</p></div>
                <div className="card p-5 text-center border-t-4 border-red-500"><p className="text-3xl font-bold text-red-500">{campDetail.bounceRate}%</p><p className="text-sm text-gray-500 mt-1">Bounce Rate</p><p className="text-xs text-gray-400">{campDetail.bouncedCount} bounced</p></div>
                <div className="card p-5 text-center border-t-4 border-orange-500"><p className="text-3xl font-bold text-orange-500">{campDetail.unsubscribeRate}%</p><p className="text-sm text-gray-500 mt-1">Unsubscribe Rate</p><p className="text-xs text-gray-400">{campDetail.unsubscribedCount} unsubs</p></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hourly Activity */}
                <div className="card p-5">
                  <h3 className="font-semibold mb-4">Hourly Activity</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={campAnalytics.hourlyBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={h => `${h}:00`} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="opened" fill="#8B5CF6" name="Opened" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="clicked" fill="#EC4899" name="Clicked" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Top Links */}
                <div className="card p-5">
                  <h3 className="font-semibold mb-4">Top Clicked Links</h3>
                  {campDetail.topLinks?.length > 0 ? (
                    <div className="space-y-2">
                      {campDetail.topLinks.map((l: any, i: number) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate flex-1 flex items-center gap-1"><ExternalLink className="w-3 h-3 flex-shrink-0" />{l.url.replace(/https?:\/\//, "")}</a>
                          <span className="text-sm font-semibold ml-3">{l.clicks}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-400 text-sm">No click data</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Geographic Breakdown */}
                <div className="card p-5">
                  <h3 className="font-semibold mb-4">Geographic Breakdown</h3>
                  {campAnalytics.countryBreakdown?.length > 0 ? (
                    <div className="space-y-2">
                      {campAnalytics.countryBreakdown.slice(0, 8).map((c: any, i: number) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{c.country}</span>
                          <div className="flex items-center gap-2"><div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${(c.count / campAnalytics.totalEvents) * 100}%` }} /></div><span className="text-xs text-gray-500 w-8 text-right">{c.count}</span></div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-400 text-sm">No geographic data</p>}
                </div>

                {/* Browser Breakdown */}
                <div className="card p-5">
                  <h3 className="font-semibold mb-4">Browser Breakdown</h3>
                  {campAnalytics.browserBreakdown?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={campAnalytics.browserBreakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="browser" type="category" tick={{ fontSize: 11 }} width={70} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#EC4899" radius={[0, 4, 4, 0]} name="Events" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-sm">No browser data</p>}
                </div>
              </div>
            </div>
          ) : !selectedCampId ? (
            <div className="card p-8 text-center">
              <Activity className="w-12 h-12 text-violet-400 mx-auto mb-3" /><p className="text-gray-500">Select a sent campaign to view analytics</p>
            </div>
          ) : null}
        </div>
      )}

      {/* ═══════ DOMAINS TAB ═══════ */}
      {tab === "domains" && (
        <div className="space-y-4">
          {domains?.length ? domains.map((d: any) => (
            <div key={d.id} className="card p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.status === "verified" ? "bg-green-100" : d.status === "pending" ? "bg-yellow-100" : "bg-red-100"}`}>
                    <Globe className={`w-5 h-5 ${d.status === "verified" ? "text-green-600" : d.status === "pending" ? "text-yellow-600" : "text-red-600"}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2"><h3 className="font-semibold">{d.domain}</h3><Badge text={d.status} map={STATUS_COLORS} />{d.isDefault && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Default</span>}</div>
                    {d.verifiedAt && <p className="text-xs text-gray-400">Verified {new Date(d.verifiedAt).toLocaleDateString()}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {d.status === "pending" && <button onClick={() => verifyDomainM.mutate(d.id)} className="text-xs text-green-600 hover:underline flex items-center gap-1"><Check className="w-3 h-3" /> Verify</button>}
                  <button onClick={() => { if (confirm("Remove domain?")) deleteDomainM.mutate(d.id); }} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {/* DNS Records */}
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: "SPF Record", value: d.spfRecord },
                  { label: "DKIM Record", value: d.dkimRecord },
                  { label: "DMARC Record", value: d.dmarcRecord },
                  { label: "Return-Path", value: d.returnPath },
                ].map(r => r.value && (
                  <div key={r.label} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">{r.label}</span>
                      <button onClick={() => copyDns(r.value!, r.label)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        {copiedDns === r.label ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 font-mono truncate">{r.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )) : (
            <div className="card p-8 text-center">
              <Globe className="w-12 h-12 text-emerald-400 mx-auto mb-3" /><p className="text-gray-500">No sending domains configured</p>
              <button onClick={() => setShowAddDomain(true)} className="btn-primary mt-3">Add Domain</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* MODALS                                         */}
      {/* ═══════════════════════════════════════════════ */}

      {/* Create List Modal */}
      {showCreateList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateList(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4"><h2 className="text-lg font-semibold text-white">New Email List</h2></div>
            <form onSubmit={e => { e.preventDefault(); createListM.mutate(listForm); }} className="p-6 space-y-4">
              <div><label className="label">Name *</label><input className="input" value={listForm.name} onChange={e => setListForm({ ...listForm, name: e.target.value })} required /></div>
              <div><label className="label">Description</label><textarea className="input" rows={2} value={listForm.description} onChange={e => setListForm({ ...listForm, description: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={listForm.doubleOptIn} onChange={e => setListForm({ ...listForm, doubleOptIn: e.target.checked })} className="rounded" /> Enable double opt-in</label>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowCreateList(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Create</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateTemplate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4"><h2 className="text-lg font-semibold text-white">New Email Template</h2></div>
            <form onSubmit={e => { e.preventDefault(); createTplM.mutate(tplForm); }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div><label className="label">Template Name *</label><input className="input" value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })} required /></div>
              <div><label className="label">Subject Line *</label><input className="input" value={tplForm.subject} onChange={e => setTplForm({ ...tplForm, subject: e.target.value })} required /></div>
              <div><label className="label">Preheader</label><input className="input" value={tplForm.preheader} onChange={e => setTplForm({ ...tplForm, preheader: e.target.value })} placeholder="Preview text in inbox" /></div>
              <div><label className="label">Category</label><select className="input" value={tplForm.category} onChange={e => setTplForm({ ...tplForm, category: e.target.value })}><option value="newsletter">Newsletter</option><option value="welcome">Welcome</option><option value="promo">Promotion</option><option value="general">General</option><option value="re-engagement">Re-engagement</option><option value="transactional">Transactional</option></select></div>
              <div><label className="label">HTML Body *</label><textarea className="input min-h-[150px] font-mono text-xs" value={tplForm.htmlBody} onChange={e => setTplForm({ ...tplForm, htmlBody: e.target.value })} required /></div>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowCreateTemplate(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Create</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateCampaign(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4"><h2 className="text-lg font-semibold text-white">New Email Campaign</h2></div>
            <form onSubmit={e => { e.preventDefault(); createCampM.mutate(campForm); }} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div><label className="label">Campaign Name *</label><input className="input" value={campForm.name} onChange={e => setCampForm({ ...campForm, name: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Type</label><select className="input" value={campForm.type} onChange={e => setCampForm({ ...campForm, type: e.target.value })}><option value="regular">Regular</option><option value="ab_test">A/B Test</option><option value="automated">Automated</option></select></div>
                <div><label className="label">Email List *</label><select className="input" value={campForm.listId} onChange={e => setCampForm({ ...campForm, listId: e.target.value })} required><option value="">Select list...</option>{lists?.map((l: any) => <option key={l.id} value={l.id}>{l.name} ({l._count?.subscribers || 0})</option>)}</select></div>
              </div>
              <div><label className="label">Template *</label><select className="input" value={campForm.templateId} onChange={e => setCampForm({ ...campForm, templateId: e.target.value })} required><option value="">Select template...</option>{templates?.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
              <div><label className="label">Subject Line *</label><input className="input" value={campForm.subject} onChange={e => setCampForm({ ...campForm, subject: e.target.value })} required /></div>
              <div><label className="label">Preheader</label><input className="input" value={campForm.preheader} onChange={e => setCampForm({ ...campForm, preheader: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">From Name</label><input className="input" value={campForm.fromName} onChange={e => setCampForm({ ...campForm, fromName: e.target.value })} placeholder="MigraHosting" /></div>
                <div><label className="label">From Email</label><input className="input" type="email" value={campForm.fromEmail} onChange={e => setCampForm({ ...campForm, fromEmail: e.target.value })} placeholder="newsletter@..." /></div>
              </div>
              <div><label className="label">Schedule (optional)</label><input className="input" type="datetime-local" value={campForm.scheduledAt} onChange={e => setCampForm({ ...campForm, scheduledAt: e.target.value })} /></div>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowCreateCampaign(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary"><Send className="w-4 h-4" /> Create</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Add Subscriber Modal */}
      {showAddSubscriber && selectedListId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddSubscriber(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4"><h2 className="text-lg font-semibold text-white">Add Subscriber</h2></div>
            <form onSubmit={e => { e.preventDefault(); addSubM.mutate({ ...subForm, name: `${subForm.firstName} ${subForm.lastName}`.trim() }); }} className="p-6 space-y-4">
              <div><label className="label">Email *</label><input className="input" type="email" value={subForm.email} onChange={e => setSubForm({ ...subForm, email: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">First Name</label><input className="input" value={subForm.firstName} onChange={e => setSubForm({ ...subForm, firstName: e.target.value })} /></div>
                <div><label className="label">Last Name</label><input className="input" value={subForm.lastName} onChange={e => setSubForm({ ...subForm, lastName: e.target.value })} /></div>
              </div>
              <div><label className="label">Tags</label><input className="input" value={subForm.tags} onChange={e => setSubForm({ ...subForm, tags: e.target.value })} placeholder="Comma separated tags" /></div>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowAddSubscriber(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Add</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Add Domain Modal */}
      {showAddDomain && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddDomain(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4"><h2 className="text-lg font-semibold text-white">Add Sending Domain</h2></div>
            <form onSubmit={e => { e.preventDefault(); addDomainM.mutate(domainForm); }} className="p-6 space-y-4">
              <div><label className="label">Domain *</label><input className="input" value={domainForm.domain} onChange={e => setDomainForm({ domain: e.target.value })} placeholder="example.com" required /></div>
              <p className="text-xs text-gray-500">After adding, you'll need to configure DNS records for SPF, DKIM, and DMARC verification.</p>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowAddDomain(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Add Domain</button></div>
            </form>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPreview(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 px-6 py-4 flex items-center justify-between">
              <div><h2 className="text-lg font-semibold text-white">{showPreview.name}</h2><p className="text-xs text-white/70">Subject: {showPreview.subject}</p></div>
              <button onClick={() => setShowPreview(null)} className="text-white/80 hover:text-white text-xl">×</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[65vh]">
              <div className="border border-gray-200 rounded-lg overflow-hidden" dangerouslySetInnerHTML={{ __html: showPreview.htmlBody }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
